import React, { useContext, useEffect, useMemo, useState, useRef } from 'react';
import ReactDOMServer from 'react-dom/server';
import axios from "axios";
import { Link, navigate, useQueryParams } from 'raviger';
import { Button, Card, CardGroup, Col, Collapse, Form, FormControl, InputGroup, ListGroup, Modal, OverlayTrigger, Row, Spinner, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBan, faInfoCircle, faGlobe, faCut, faEnvelope, faLink, faMapMarkerAlt, faMedkit, faUserAltSlash,
  faCaretDown,
  faChevronDown
} from '@fortawesome/free-solid-svg-icons';
import {
  faDotCircle
} from '@fortawesome/free-regular-svg-icons';
import { faPipe } from '@fortawesome/pro-thin-svg-icons';
import { Typeahead, Menu, MenuItem } from 'react-bootstrap-typeahead';
import { faHandsHolding, faChevronDoubleDown, faChevronDoubleUp, faClawMarks, faFolderMedical, faPhoneRotary } from '@fortawesome/pro-solid-svg-icons';
import Select, { components } from 'react-select';
import L from "leaflet";
import { Circle, MapContainer, Marker, Tooltip as MapTooltip, ScaleControl, TileLayer, useMap, useMapEvents } from "react-leaflet";
import { useMark, useSubmitting, useDataImg, useLocationWithRoutes } from '../hooks';
import Pagination from '@mui/material/Pagination';
import PaginationItem from '@mui/material/PaginationItem';
import Stack from '@mui/material/Stack';
import Header from '../components/Header';
import Scrollbar from '../components/Scrollbars';
import { titleCase } from '../components/Utils';
import { ITEMS_PER_PAGE } from '../constants';
import { catColorChoices, dogColorChoices, horseColorChoices, otherColorChoices, statusChoices, vetStatusChoices } from './constants';
import AnimalCoverImage from '../components/AnimalCoverImage';
import { printAnimalCareSchedule, printAllAnimalCareSchedules } from './Utils';
import { MapResizer } from '../components/Map';
import Paginator from '../components/Paginator';
import { AuthContext } from "../accounts/AccountsReducer";
import { SystemErrorContext } from '../components/SystemError';
import ShelterlyPrintifyButton from '../components/ShelterlyPrintifyButton';
import ActionsDropdown from '../components/ActionsDropdown';
import '../assets/styles.css';

const ToggleButton = ({ isOpen, onClick }) => (
  <span
    style={{zIndex:"11", marginLeft:"-25px"}}
    className="toggle-button"
    onClick={onClick}
    onMouseDown={(e) => {
      // Prevent input from losing focus.
      e.preventDefault();
    }}>
    {<>
      <FontAwesomeIcon icon={faPipe} style={{zIndex:"9999", height:"20px", marginLeft:"1px"}} transform={'grow-1 down-8 left-12'} color="#cccccc" size="sm" />
      <FontAwesomeIcon icon={faChevronDown} style={{zIndex:"9999", WebkitTextStroke: '0.5px', fontWeight:"bold"}} transform={'down-8 left-4'} color="#cccccc" size="sm" />
    </>}
  </span>
);

function LocationMarker(props) {
  const markerRef = useRef(null);

  const pinIconHTML = ReactDOMServer.renderToString(<FontAwesomeIcon color="red" size="lg" className="icon-border" icon={faMapMarkerAlt} />);
  const pinMarkerIcon = new L.DivIcon({
    html: pinIconHTML,
    iconSize: [0, 0],
    iconAnchor: [5, 10],
    className: "pin-icon",
    popupAnchor: null,
    shadowUrl: null,
    shadowSize: null,
    shadowAnchor: null
  });

  const map = useMapEvents({
    // click(e) {
    //   map.locate()
    //   props.setOptions({...props.options, latlng: e.latlng});
    // },
  });

  const eventHandlers = useMemo(
    () => ({
      dragend() {
        const marker = markerRef.current;
        // const map = mapRef.current
        if (marker !== null) {
          const latLon = marker.getLatLng();
          props.setOptions({...props.options, latlng: latLon});
          map.setView(latLon);
        }
      },
    }),
    [] // Empty dependency array means this memoized value only recomputes once
  );

  // Hook handling option changes.
  useEffect(() => {
    let zoom = (props.options.radius === 1) && (map.getZoom() > 12) ? 14 : (props.options.radius === 2) && (map.getZoom() > 11) ? 13 : map.getZoom() > 10 ? 12 : null;
    if (zoom && props.options.latlng && props.options.latlng.lat) {
      map.flyTo(props.options.latlng);
      setTimeout(() => {
        map.setZoom(zoom);
      }, 500);
    }
  }, [props.options.radius, props.options.latlng]);

  return props.options.latlng === null ? null : (
  <>
    <Marker
      position={props.options.latlng}
      icon={pinMarkerIcon}
      draggable={true}
      eventHandlers={eventHandlers}
      ref={markerRef}
    >
      <MapTooltip autoPan={false} direction="top">
        <div>
          Lat: {+(Math.round(props.options.latlng.lat + "e+4") + "e-4")}, Lon: {+(Math.round(props.options.latlng.lng + "e+4") + "e-4")}
        </div>
      </MapTooltip>
    </Marker>
    <Circle center={props.options.latlng} color={'#ff4c4c'} radius={(props.options.radius * 1609.34)/2} interactive={false} />
  </>
  )
}

const NoOptionsMessage = props => {
  return (
    <components.NoOptionsMessage {...props}>
      <span>Select a species</span>
    </components.NoOptionsMessage>
  );
};

function AnimalSearch({ incident, organization }) {

  const { dispatch, state } = useContext(AuthContext);
  const { setShowSystemError } = useContext(SystemErrorContext);

  // Identify any query param data.
  const [queryParams] = useQueryParams();
  const {
    search = '',
  } = queryParams;

  const ownedChoices = [
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' },
  ];

  const fixedChoices = [
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' },
    { value: 'unknown', label: 'Unknown'}
  ];

  const sexChoices = [
    { value: 'M', label: 'Male' },
    { value: 'F', label: 'Female' },
    { value: '', label: 'Unknown' },
  ];

  const radiusChoices = [
    // { value: 0, label: 'None' },
    { value: 1, label: '1 Mile' },
    { value: 2, label: '2 Miles' },
    { value: 5, label: '5 Miles' },
  ];

  const publicChoices = [
    {value: 0, label: 'Private' },
    {value: 1, label: 'Public' }
  ]

  const [data, setData] = useState({animals: [], total_count: 0, isFetching: false});
  const [shelters, setShelters] = useState({options: [], isFetching: false});
  const [buildingsRooms, setBuildingsRooms] = useState({options: [], isFetching: false});
  const [speciesChoices, setSpeciesChoices] = useState([]);
  const [options, setOptions] = useState({id:null, species:[], status:[], vet_status:[], sex:[], owned:null, sheltered:null, pcolor:'', fixed:[], aggressive:[], injured:[], confined:[], aco_required:[], latlng:null, radius:null, shelter:[], building:[], room:[], public: null});
  const [searchTerm, setSearchTerm] = useState(search);
  const [showFilters, setShowFilters] = useState(false);
  const [isDisabled, setIsDisabled] = useState(true);
  const [goToID, setGoToID] = useState('');
  const [showNotFound, setShowNotFound] = useState(false);
  const [triggerRefresh, setTriggerRefresh] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [colorOptions, setColorOptions] = useState([]);
  const tempSearchTerm = useRef(null);
  const speciesRef = useRef(null);
  const statusRef = useRef(null);
  const vetStatusRef = useRef(null);
  const sexRef = useRef(null);
  const ownedRef = useRef(null);
  const shelteredRef = useRef(null);
  const aggressiveRef = useRef(null);
  const injuredRef = useRef(null);
  const confinedRef = useRef(null);
  const acoRequiredRef = useRef(null);
  const fixedRef = useRef(null);
  const pcolorRef = useRef(null);
  const shelterRef = useRef(null);
  const buildingRoomRef = useRef(null);
  const publicRef = useRef(null);
  const radiusRef = useRef(null);
  const mapRef = useRef(null);
  const [bounds, setBounds] = useState(null);
  const [page, setPage] = useState(1);
  const [numPages, setNumPages] = useState(1);
  const { markInstances } = useMark();
  const {
    isSubmitting,
    handleSubmitting,
    submittingComplete,
    submittingLabel
  } = useSubmitting();
  const { promiseImage, getBase64Image } = useDataImg();
  const [lazyAnimalImages, setLazyAnimalImages] = useState([]);
  const { getFullLocationFromPath } = useLocationWithRoutes();

  function findLazyAnimalImage (animalId) {
    return lazyAnimalImages.find((lazyAnimal) => lazyAnimal.id === animalId);
  }
  function addLazyAnimalImage (animalId, animalImage) {
    setLazyAnimalImages((prevState) => {
      prevState.push({
        id: animalId,
        image: animalImage
      });
      return prevState;
    })
  }

  const colorChoices = {'':[], 'dog':dogColorChoices, 'cat':catColorChoices, 'horse':horseColorChoices, 'other':otherColorChoices}
  // Update searchTerm when field input changes.
  const handleChange = event => {
    tempSearchTerm.current.value = event.target.value;
  };

  const handleIDChange = async event => {
    setGoToID(event.target.value);
  };

  // Use searchTerm to filter animals.
  const handleSubmit = async event => {
    event.preventDefault();
    setSearchTerm(tempSearchTerm.current.value);
    setPage(1);
  };

  const handleShowFilters = () => {
    setShowFilters(!showFilters);
  };

  const handleClear = () => {
    speciesRef.current.clear();
    statusRef.current.clear();
    vetStatusRef.current.clear();
    sexRef.current.clear();
    aggressiveRef.current.clear();
    fixedRef.current.clear();
    injuredRef.current.clear();
    confinedRef.current.clear();
    acoRequiredRef.current.clear();
    pcolorRef.current.clear();
    shelterRef.current.clear();
    buildingRoomRef.current.clear();
    radiusRef.current.clearValue();
    ownedRef.current.clearValue();
    shelteredRef.current.clearValue();
    publicRef.current.clearValue();
    setOptions({id:null, species:[], status:[], vet_status:[], sex:[], owned:null, sheltered:null, pcolor:[], aggressive:[], confined:[], injured:[], aco_required:[], fixed:[], shelter:[], building:[], room:[], latlng:null, radius:null, public:null});
    setTriggerRefresh(!triggerRefresh);
  };

  function buildAnimalUrl(animal) {
    return getFullLocationFromPath(`/${organization}/${incident}/animals/${animal.id_for_incident}`)
  }

  const handleDownloadPdfClick = (animalId) => {
    const animal = data.animals.find((animal) => animal.id === animalId);
    animal.url = buildAnimalUrl(animal);
    printAnimalCareSchedule(animal);
  }

  const handleGo = async () => {
    setIsLoading(true);
    await axios.get('/animals/api/incident/' + (state ? state.incident.id : 'undefined') + '/animal/' + goToID + '/')
    .then(response => {
      navigate("/" + organization + "/" + incident + "/animals/" + goToID);
    })
    .catch(error => {
      if (error.response && error.response.status === 404) {
        // Handle the 404 specifically
        setShowNotFound(true)
      } else {
        setShowSystemError(true);
      }
    })
    .finally(() => setIsLoading(false));
  }

  const handlePrintAllClick = async () => {
    setIsLoading(true);

    let animals = [];
    let nextUrl = '/animals/api/animal/?page=1&search=' + searchTerm +'&incident=' + incident + '&organization=' + organization
    do {
      const response = await axios.get(nextUrl, {
        params: {
          status: options.status,
          vet_status: options.vet_status,
          species: options.species,
          sex: options.sex,
          fixed: options.fixed,
          aggressive: options.aggressive,
          aco_required: options.aco_required,
          injured: options.injured,
          confined: options.confined,
          owned: options.owned,
          sheltered: options.sheltered,
          pcolor: options.pcolor,
          scolor: options.scolor,
          shelter: options.shelter,
          public: options.public,
          latlng: options.latlng,
          radius: options.radius,
        },
      })
      .catch(error => {
        if (error.response) {
          setShowSystemError(true);
        }
      });

      animals.push(...response.data.results);
      nextUrl = response.data.next;
      if (nextUrl) {
        nextUrl = '/animals/' + response.data.next.split('/animals/')[1];
      }
    } while(nextUrl != null)

    handleSubmitting()
      .then(() => animals.map((animal) => ({
        ...animal,
        url: buildAnimalUrl(animal)
      })))
      .then((supplementedAnimals) => printAllAnimalCareSchedules(supplementedAnimals))
      .then(submittingComplete)
      .finally(() => setIsLoading(false));
  }

  const customStyles = {
    // For the select it self, not the options of the select
    control: (styles, { isDisabled}) => {
      return {
        ...styles,
        color: '#FFF',
        cursor: isDisabled ? 'not-allowed' : 'default',
        backgroundColor: isDisabled ? '#DFDDDD' : 'white',
        height: 35,
        minHeight: 35,
        marginBottom: "10px"
      }
    },
    option: provided => ({
      ...provided,
      color: 'black'
    }),
    menu: provided => ({ ...provided, zIndex: 9999 })
  };

  // Hook for initializing data.
  useEffect(() => {
    let unmounted = false;
    let source = axios.CancelToken.source();

    tempSearchTerm.current.focus();

    const fetchSpecies = () => {
      setSpeciesChoices([]);
      // Fetch Species data.
      axios.get('/animals/api/species/', {
        cancelToken: source.token,
      })
      .then(response => {
        if (!unmounted) {
          let species_options = [];
          response.data.forEach(result => {
            // Build species option list.
            species_options.push({value: result.name, label: result.name});
          });
          setSpeciesChoices(species_options);
        }
      })
      .catch(error => {
        if (!unmounted) {
          setShelters({options: []});
          setShowSystemError(true);
        }
      });
    };
    fetchSpecies();

    const fetchShelters = () => {
      setShelters({options: [], isFetching: true});
      setBuildingsRooms({options: [], isFetching: true});
      // Fetch Shelter data.
      axios.get('/shelter/api/shelter/?incident=' + incident + '&organization=' + organization, {
        cancelToken: source.token,
      })
      .then(response => {
        if (!unmounted) {
          let shelter_options = [];
          let building_room_options = [];
          response.data.forEach(shelter => {
            // Build shelter option list.
            shelter_options.push({value: shelter.id, label: shelter.name});
            shelter.buildings.forEach(building => {
              building_room_options.push({value: building.id, label: building.name, shelter:shelter.id});
              building.rooms.forEach(room => {
                building_room_options.push({value: room.id, label: building.name + ' - ' + room.name, building:room.building, shelter:shelter.id});
              });
            });
          });
          setShelters({options: shelter_options, isFetching:false});
          setBuildingsRooms({options: building_room_options, isFetching:false});
        }
      })
      .catch(error => {
        if (!unmounted) {
          setShelters({options: [], isFetching: false});
          setShowSystemError(true);
        }
      });
    };
    fetchShelters();
    // Cleanup.
    return () => {
      unmounted = true;
      source.cancel();
    };
  }, []);

  // Hook for initializing data.
  useEffect(() => {
    let unmounted = false;
    let source = axios.CancelToken.source();

    const fetchAnimals = async () => {
      setData({animals: [], total_count: 0, isFetching: true});

      // Fetch Animal data.
      await axios.get('/animals/api/animal/?page=' + page + '&search=' + searchTerm +'&incident=' + incident + '&organization=' + organization, {
        cancelToken: source.token,
        params: {
          status: options.status,
          vet_status: options.vet_status,
          species: options.species,
          sex: options.sex,
          owned: options.owned,
          sheltered: options.sheltered,
          fixed: options.fixed,
          aggressive: options.aggressive,
          aco_required: options.aco_required,
          injured: options.injured,
          confined: options.confined,
          pcolor: options.pcolor,
          scolor: options.scolor,
          shelter: options.shelter,
          building: options.building,
          room: options.room,
          public: options.public,
          latlng: options.latlng,
          radius: options.radius,
        },
      })
      .then(async (response) => {
        if (!unmounted) {
          setNumPages(Math.ceil(response.data.count / ITEMS_PER_PAGE));
          setData({animals: response.data.results, total_count: response.data.count, isFetching: false});

          // highlight search terms
          markInstances(searchTerm);

          let bounds_array = [];
          let used = [];
          if (options.latlng) {
            bounds_array.push([options.latlng['lat'], options.latlng['lng']]);
          }
          for (const animal of response.data.results) {
            if (!options.latlng && animal.latitude && animal.longitude) {
              if (!used.includes((parseFloat(animal.latitude), parseFloat(animal.longitude)))) {
                bounds_array.push([parseFloat(animal.latitude), parseFloat(animal.longitude)]);
                used.push((parseFloat(animal.latitude), parseFloat(animal.longitude)));
              }
            }

            // lazy load animal front_image
            const lazyAnimalImage = findLazyAnimalImage(animal.id);
            if (lazyAnimalImage?.image) {
              animal.lazyImage = lazyAnimalImage.image
            } else if (animal.front_image) {
              const imgData = await promiseImage(animal.front_image);
              animal.lazyImage = imgData;
              addLazyAnimalImage(animal.id, imgData);
            }
          }
          // Use the Incident bounds if we don't have any animal locations.
          if (bounds_array.length === 0) {
            bounds_array.push([parseFloat(state.incident.coordinates[0])+.04,parseFloat(state.incident.coordinates[1])-.04]);
            bounds_array.push([parseFloat(state.incident.coordinates[0])-.04,parseFloat(state.incident.coordinates[1])+.04]);
          }
          setBounds(bounds_array.length > 0 ? bounds_array : null);
        }
      })
      .catch(error => {
        if (!unmounted) {
          setData({animals: [], total_count: 0, isFetching: false});
          setShowSystemError(true);
        }
      });
    };
    fetchAnimals();

    // Cleanup.
    return () => {
      unmounted = true;
      source.cancel();
    };
  }, [incident, searchTerm, triggerRefresh, page]);

  // Hook handling option changes.
  useEffect(() => {
    setIsDisabled(
      !(
        options.id ||
        options.species ||
        options.status ||
        options.vet_status ||
        options.sex ||
        options.pcolor ||
        options.aggressive ||
        options.fixed ||
        options.injured ||
        options.confined ||
        options.aco_required ||
        options.latlng ||
        options.shelter ||
        options.building ||
        options.room ||
        options.owned ||
        options.sheltered ||
        options.public !== null
      )
    );
  }, [options]);

  // Hook handling color options when species changes.
  useEffect(() => {
    let color_choices = [];
    if (options.species.includes('dog')) {
      color_choices = color_choices.concat(colorChoices['dog']);
    }
    if (options.species.includes('cat')) {
      color_choices = color_choices.concat(colorChoices['cat']);
    }
    if (options.species.includes('horse')) {
      color_choices = color_choices.concat(colorChoices['horse']);
    }
    if (options.species.filter(specie => !['dog', 'cat', 'horse'].includes(specie)).length > 0) {
      color_choices = color_choices.concat(colorChoices['other']);
    }
    // Dedupe and sort.
    setColorOptions(color_choices.filter((v, i, a) => a.findIndex(t => t.value === v.value) === i).sort(function(a, b) {
      return a['label'].localeCompare(b['label']);
    }));
  }, [options.species]);

  return (
    <div className="ms-2 me-2">
      <Header>Search Animals</Header>
      <Form onSubmit={handleSubmit}>
        <Row>
          <Col xs="2" style={{maxWidth:"150px", marginRight:"-10px", paddingRight:"0px"}}>
            <InputGroup>
              <FormControl
                type="text"
                placeholder="ID #"
                name="searchIDTerm"
                onChange={handleIDChange}
              />
              <Button variant="outline-light" type="submit" disabled={isLoading || !goToID} style={{borderRadius:"0 5px 5px 0", color:"white"}} onClick={(e) => {handleGo()}}>Go</Button>
            </InputGroup>
          </Col>
          <Col>
            <InputGroup>
              <FormControl
                type="text"
                placeholder="Search"
                name="searchTerm"
                onChange={handleChange}
                ref={tempSearchTerm}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSubmit(e);
                  }
                }}
              />
              <Button variant="outline-light" type="submit" style={{borderRadius:"0 5px 5px 0", color:"white"}}>Search
                <OverlayTrigger
                  key={"search-information"}
                  placement="right"
                  overlay={
                    <Tooltip id={`tooltip-search-information`}>
                      Searchable fields: name, microchip, external ID, address fields, and owner last names.
                    </Tooltip>
                  }
                >
                  <FontAwesomeIcon icon={faInfoCircle} className="ms-1 fa-move-up" size="sm" inverse />
                </OverlayTrigger>
              </Button>
              <Button variant="outline-light" className="ms-1 me-1 rounded" style={{color:"white"}} onClick={handleShowFilters}>Advanced {showFilters ? <FontAwesomeIcon icon={faChevronDoubleUp} className="fa-move-up" size="sm" /> : <FontAwesomeIcon icon={faChevronDoubleDown} className="fa-move-up" size="sm" />}</Button>
              {isLoading ? (
                <div className="d-flex" style={{width:"148px", justifyContent:"center"}}>
                <Spinner
                  {...{
                    as: 'span',
                    animation: 'border',
                    size: undefined,
                    role: 'status',
                    'aria-hidden': 'true',
                    variant: 'light',
                    style: {
                      height: '2rem',
                      width: '2rem',
                      marginBottom: '0.25rem'
                    }
                  }}
                />
                </div>
              ) : (
              <ActionsDropdown title={"Download All" + " (" + `${data.total_count}` + ")"} search={true} disabled={data.isFetching || data.animals.length === 0} variant="outline-light">
                <ShelterlyPrintifyButton
                  id="animal-details-animal-care-schedule"
                  spinnerSize={1.5}
                  tooltipPlacement='right'
                  label='Animal Care Schedules'
                  printFunc={(event) => handlePrintAllClick(event)}
                  disabled={data.isFetching}
                  noOverlay={true}
                />
              </ActionsDropdown>
            )}
            </InputGroup>
          </Col>
        </Row>
        <Collapse in={showFilters}>
          <div>
          <Card className="border rounded d-flex mt-3" style={{width:"100%"}}>
            <Card.Body style={{marginBottom:"-16px"}}>
              <Row className="gx-2">
                <Col xs={"9"} style={{textTransform:"capitalize"}}>
                  <Typeahead
                    id="species"
                    name="species"
                    multiple
                    onChange={(values) => {
                      pcolorRef.current.clear();
                      setOptions({...options, species: values.map(option => option.value), pcolor: []});
                    }}
                    ref={speciesRef}
                    options={speciesChoices}
                    placeholder="Filter by Species..."
                    className="species-typeahead mb-2 no-wrap-container w-100"
                    selected={speciesChoices.filter(choice => options.species.includes(choice.value))}
                    inputProps={{ className: 'custom-typeahead-input' }}
                  >
                    {({ isMenuShown, toggleMenu }) => (
                      <ToggleButton isOpen={isMenuShown} onClick={(e) => toggleMenu()} />
                    )}
                  </Typeahead>
                  <Typeahead
                    id="status"
                    name="status"
                    multiple
                    onChange={(values) => {
                      setOptions({...options, status: values.map(option => option.value)});
                    }}
                    ref={statusRef}
                    options={statusChoices}
                    placeholder="Filter by Status..."
                    className="status-typeahead mb-2 no-wrap-container w-100"
                    selected={statusChoices.filter(choice => options.status.includes(choice.value))}
                    inputProps={{ className: 'custom-typeahead-input' }}
                  >
                    {({ isMenuShown, toggleMenu }) => (
                      <ToggleButton isOpen={isMenuShown} onClick={(e) => toggleMenu()} />
                    )}
                  </Typeahead>
                  <Typeahead
                    id="vet_status"
                    name="vet_status"
                    multiple
                    onChange={(values) => {
                      setOptions({...options, vet_status: values.map(option => option.value)});
                    }}
                    ref={vetStatusRef}
                    options={vetStatusChoices}
                    placeholder="Filter by Vet Status..."
                    className="vet_status-typeahead mb-2 no-wrap-container w-100"
                    selected={vetStatusChoices.filter(choice => options.vet_status.includes(choice.value))}
                    inputProps={{ className: 'custom-typeahead-input' }}
                  >
                    {({ isMenuShown, toggleMenu }) => (
                      <ToggleButton isOpen={isMenuShown} onClick={(e) => toggleMenu()} />
                    )}
                  </Typeahead>
                  <OverlayTrigger
                    key={"disabled"}
                    placement="top"
                    overlay={
                      <Tooltip id={`tooltip-disabled`} hidden={options.species.length > 0} style={{marginBottom:"-15px"}}>
                        {options.species.length === 0 ? "One or more species must be selected to pick a color." : ""}
                      </Tooltip>
                    }
                  >
                    <span>
                      <Typeahead
                        id="pcolor"
                        name="pcolor"
                        multiple
                        onChange={(values) => {
                          setOptions({...options, pcolor: values.map(option => option.value)});
                        }}
                        ref={pcolorRef}
                        disabled={options.species.length === 0}
                        options={colorOptions}
                        placeholder="Filter by Primary Color..."
                        className="pcolor-typeahead mb-2 no-wrap-container w-100"
                        // selected={colorChoices[options.species] && colorChoices[options.species].filter(choice => options.pcolor.includes(choice.value))}
                        inputProps={{ className: 'custom-typeahead-input' }}
                      >
                        {({ isMenuShown, toggleMenu }) => (
                          <ToggleButton isOpen={isMenuShown} onClick={(e) => toggleMenu()} />
                        )}
                      </Typeahead>
                    </span>
                  </OverlayTrigger>
                  <Typeahead
                    id="shelter"
                    name="shelter"
                    multiple
                    onChange={(values) => {
                      setOptions({...options, shelter: values.map(value => value.value), building:[...buildingsRooms.options.filter(option => options.building.includes(option.value) && values.map(value => value.value).includes(option.shelter)).map(option => option.value)], room:[...buildingsRooms.options.filter(option => options.room.includes(option.value) && values.map(value => value.value).includes(option.shelter)).map(option => option.value)]});
                    }}
                    ref={shelterRef}
                    options={shelters.options}
                    placeholder="Filter by Shelter..."
                    className="shelter-typeahead mb-2 no-wrap-container w-100"
                    selected={shelters.options.filter(choice => options.shelter.includes(choice.value))}
                    inputProps={{ className: 'custom-typeahead-input' }}
                  >
                    {({ isMenuShown, toggleMenu }) => (
                      <ToggleButton isOpen={isMenuShown} onClick={(e) => toggleMenu()} />
                    )}
                  </Typeahead>
                  <OverlayTrigger
                    key={"disabled-building-room"}
                    placement="top"
                    overlay={
                      <Tooltip id={`tooltip-disabled-building-room`} hidden={options.shelter.length > 0} style={{marginBottom:"-15px"}}>
                        {options.shelter.length === 0 ? "One or more shelters must be selected to pick a building and/or room." : ""}
                      </Tooltip>
                    }
                  >
                    <span>
                      <Typeahead
                        id="building-room"
                        name="building-room"
                        multiple
                        onChange={(values) => {
                          setOptions({...options, building:values.map(value => value.building ? null : value.value), room:values.map(value => value.building && !values.filter(test => !test.building).map(test => test.value).includes(value.building) ? value.value : null) });
                        }}
                        ref={buildingRoomRef}
                        disabled={options.shelter.length === 0}
                        options={buildingsRooms.options.filter(option => options.shelter.includes(option.shelter) && !options.building.includes(option.building))}
                        placeholder="Filter by Building/Room..."
                        className="building-room-typeahead mb-2 no-wrap-container w-100"
                        selected={buildingsRooms.options.filter(choice => choice.building ? options.room.includes(choice.value) : options.building.includes(choice.value))}
                        inputProps={{ className: 'custom-typeahead-input' }}
                      >
                        {({ isMenuShown, toggleMenu }) => (
                          <ToggleButton isOpen={isMenuShown} onClick={(e) => toggleMenu()} />
                        )}
                      </Typeahead>
                    </span>
                  </OverlayTrigger>
                </Col>
                <Col className="flex-grow-1" xs="3">
                  <Select
                    label="Owned"
                    id="owned"
                    name="owned"
                    type="text"
                    placeholder="Select Owned"
                    options={ownedChoices}
                    styles={customStyles}
                    isClearable={true}
                    ref={ownedRef}
                    onChange={(instance) => {
                      setOptions({...options, owned: instance ? instance.value : null})
                    }}
                  />
                  <Select
                    label="Sheltered"
                    id="sheltered"
                    name="sheltered"
                    type="text"
                    placeholder="Select Sheltered"
                    options={ownedChoices}
                    styles={customStyles}
                    isClearable={true}
                    ref={shelteredRef}
                    onChange={(instance) => {
                      setOptions({...options, sheltered: instance ? instance.value : null})
                    }}
                  />
                  <Select
                    inputId="public"
                    name="public"
                    placeholder="Select Public"
                    options={publicChoices}
                    styles={customStyles}
                    isClearable
                    ref={publicRef}
                    value={publicChoices.find(opt => opt.value === options.public) || null}
                    onChange={(instance) => {
                      setOptions(prev => ({
                        ...prev,
                        public: instance ? instance.value : null
                      }))
                    }}
                  />
                  <Button className="btn btn-primary" style={{maxHeight:"35px", width:"100%"}} onClick={() => {tempSearchTerm.current.value !== searchTerm ? setSearchTerm(tempSearchTerm.current.value) : setTriggerRefresh(!triggerRefresh);}} disabled={isDisabled}>Apply</Button>
                  <Button variant="outline-light" style={{maxHeight:"35px", width:"100%", marginTop:"9px", marginBottom:"10px"}} onClick={handleClear}>Clear</Button>
                </Col>
              </Row>
              <Row className="gx-2">
                <Col xs={"3"} style={{textTransform:"capitalize"}}>
                  <Typeahead
                    id="sex"
                    name="sex"
                    multiple
                    onChange={(values) => {
                      setOptions({...options, sex: values.map(option => option.value)});
                    }}
                    ref={sexRef}
                    options={sexChoices}
                    placeholder="Filter by Sex..."
                    className="sex-typeahead mb-2 no-wrap-container w-100"
                    selected={sexChoices.filter(choice => options.sex.includes(choice.value))}
                    inputProps={{ className: 'custom-typeahead-input' }}
                  >
                    {({ isMenuShown, toggleMenu }) => (
                      <ToggleButton isOpen={isMenuShown} onClick={(e) => toggleMenu()} />
                    )}
                  </Typeahead>
                </Col>
                <Col xs={"3"}>
                  <Typeahead
                    id="aggressive"
                    name="aggressive"
                    multiple
                    onChange={(values) => {
                      setOptions({...options, aggressive: values.map(option => option.value)});
                    }}
                    ref={aggressiveRef}
                    options={fixedChoices}
                    placeholder="Filter by Aggressive..."
                    className="aggressive-typeahead mb-2 no-wrap-container w-100"
                    selected={fixedChoices.filter(choice => options.aggressive.includes(choice.value))}
                    inputProps={{ className: 'custom-typeahead-input' }}
                  >
                    {({ isMenuShown, toggleMenu }) => (
                      <ToggleButton isOpen={isMenuShown} onClick={(e) => toggleMenu()} />
                    )}
                  </Typeahead>
                </Col>
                <Col xs={"3"}>
                  <Typeahead
                    id="aco_required"
                    name="aco_required"
                    multiple
                    onChange={(values) => {
                      setOptions({...options, aco_required: values.map(option => option.value)});
                    }}
                    ref={acoRequiredRef}
                    options={fixedChoices}
                    placeholder="Filter by Special Handling..."
                    className="aco_required-typeahead mb-2 no-wrap-container w-100"
                    selected={fixedChoices.filter(choice => options.aco_required.includes(choice.value))}
                    inputProps={{ className: 'custom-typeahead-input' }}
                  >
                    {({ isMenuShown, toggleMenu }) => (
                      <ToggleButton isOpen={isMenuShown} onClick={(e) => toggleMenu()} />
                    )}
                  </Typeahead>
                </Col>
              </Row>
               <Row className="gx-2">
                <Col xs={"3"} style={{textTransform:"capitalize"}}>
                  <Typeahead
                    id="fixed"
                    name="fixed"
                    multiple
                    onChange={(values) => {
                      setOptions({...options, fixed: values.map(option => option.value)});
                    }}
                    ref={fixedRef}
                    options={fixedChoices}
                    placeholder="Filter by Altered..."
                    className="fixed-typeahead mb-2 no-wrap-container w-100"
                    selected={fixedChoices.filter(choice => options.fixed.includes(choice.value))}
                    inputProps={{ className: 'custom-typeahead-input' }}
                  >
                    {({ isMenuShown, toggleMenu }) => (
                      <ToggleButton isOpen={isMenuShown} onClick={(e) => toggleMenu()} />
                    )}
                  </Typeahead>
                </Col>
                <Col xs={"3"}>
                  <Typeahead
                    id="injured"
                    name="injured"
                    multiple
                    onChange={(values) => {
                      setOptions({...options, injured: values.map(option => option.value)});
                    }}
                    ref={injuredRef}
                    options={fixedChoices}
                    placeholder="Filter by Injured..."
                    className="injured-typeahead mb-2 no-wrap-container w-100"
                    selected={fixedChoices.filter(choice => options.injured.includes(choice.value))}
                    inputProps={{ className: 'custom-typeahead-input' }}
                  >
                    {({ isMenuShown, toggleMenu }) => (
                      <ToggleButton isOpen={isMenuShown} onClick={(e) => toggleMenu()} />
                    )}
                  </Typeahead>
                </Col>
                <Col xs={"3"}>
                  <Typeahead
                    id="confined"
                    name="confined"
                    multiple
                    onChange={(values) => {
                      setOptions({...options, confined: values.map(option => option.value)});
                    }}
                    ref={confinedRef}
                    options={fixedChoices}
                    placeholder="Filter by Confined..."
                    className="confined-typeahead mb-2 no-wrap-container w-100"
                    selected={fixedChoices.filter(choice => options.confined.includes(choice.value))}
                    inputProps={{ className: 'custom-typeahead-input' }}
                  >
                    {({ isMenuShown, toggleMenu }) => (
                      <ToggleButton isOpen={isMenuShown} onClick={(e) => toggleMenu()} />
                    )}
                  </Typeahead>
                </Col>
              </Row>
              <Row className="gx-2">
                <Col xs={6}>
                  <Select
                    label="Radius"
                    id="radiusDropdown"
                    name="radius"
                    type="text"
                    placeholder="Filter by Location Radius..."
                    options={radiusChoices}
                    styles={customStyles}
                    ref={radiusRef}
                    isClearable={true}
                    onChange={(instance) => {
                      let latlng = bounds.length === 1 ? {lat:bounds[0][0], lng:bounds[0][1]} : L.latLngBounds(bounds).getCenter();
                      setOptions({...options, latlng:latlng, radius: instance ? instance.value : null});
                    }}
                  />
                </Col>
              </Row>
              {options.radius ? <Row className="gx-2 ms-0 mb-2" style={{marginRight:"5px"}}>
                <Col xs={6} className="border rounded ps-0 pe-0">
                  {bounds ?
                  <MapContainer ref={mapRef} bounds={bounds.length > 1 ? bounds : null} center={bounds.length === 1 ? [bounds[0][0], bounds[0][1]] : null} zoom={13} dragging={true} keyboard={false} className="animal-search-leaflet-container">
                    <ScaleControl position="bottomleft" metric={false} />
                    <MapResizer showFilters={showFilters} bounds={bounds} />
                    <TileLayer
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
                    />
                    <LocationMarker options={options} setOptions={setOptions} />
                  </MapContainer> : ""}
                </Col>
              </Row> : ""}
            </Card.Body>
          </Card>
          </div>
        </Collapse>
      </Form>
      {data.animals.map((animal, index) => (
        <div key={animal.id} className="mt-3 border rounded">
          <div className="card-header rounded">
            <h4 style={{marginBottom:"-2px",  marginLeft:"-12px"}}>
              <OverlayTrigger
                key={"animal-details"}
                placement="top"
                overlay={
                  <Tooltip id={`tooltip-animal-details`}>
                    Animal details
                  </Tooltip>
                }
              >
                <Link href={"/" + organization + "/" + incident + "/animals/" + animal.id_for_incident}><FontAwesomeIcon icon={faDotCircle} className="me-2" inverse /></Link>
              </OverlayTrigger>
              A#{animal.id_for_incident} - {animal.animal_count > 1 ? <span>{animal.animal_count} {titleCase(animal.species_string)}</span> : <span>{animal.name ? titleCase(animal.name) : "Unknown Name"}</span>}
              <div className="float-end d-flex" style={{marginRight:"-5px"}}>
                <ShelterlyPrintifyButton
                  id="animal-search-animal-schedules"
                  spinnerSize={1.5}
                  tooltipPlacement='top'
                  tooltipText='Download Animal Care Schedule'
                  printFunc={() => handleDownloadPdfClick(animal.id)}
                />
              </div>
            </h4>
          </div>
          <CardGroup style={{marginBottom:"-6px"}}>
            <Card style={{maxWidth:"206px", maxHeight:"206px"}}>
              <Card.Body className="p-0 m-0 d-flex justify-content-center align-items-center">
                {animal.front_image && !animal.lazyImage
                  ? (
                    <Spinner animation="border" role="status">
                      <span className="visually-hidden">Loading Image...</span>
                    </Spinner>
                  )
                : (
                  <AnimalCoverImage
                    animalSpecies={animal.species_string_singular}
                    animalImageSrc={
                      animal.lazyImage
                        ? `data:image/png;base64,${getBase64Image(animal.lazyImage)}`
                        : animal.front_image
                    }
                  />
                )}
              </Card.Body>
            </Card>
            <Card style={{marginBottom:"6px", maxWidth:"335px"}}>
              <Card.Body>
                <Card.Title style={{marginTop:"-9px", marginBottom:"8px"}}>Information
                  {animal.owners.length < 1 ?
                    <OverlayTrigger
                      key={"stray"}
                      placement="top"
                      overlay={
                        <Tooltip id={`tooltip-stray`}>
                          Animal is stray
                        </Tooltip>
                      }
                    >
                      <FontAwesomeIcon icon={faUserAltSlash} size="sm" className="ms-1" />
                    </OverlayTrigger> :
                  ""}
                  {animal.confined === 'yes' ?
                    <OverlayTrigger
                      key={"confined"}
                      placement="top"
                      overlay={
                        <Tooltip id={`tooltip-confined`}>
                          Animal is confined
                        </Tooltip>
                      }
                    >
                      <FontAwesomeIcon icon={faLink} size="sm" className="ms-1" />
                    </OverlayTrigger> :
                  ""}
                  {animal.aggressive === 'yes' ?
                    <OverlayTrigger
                      key={"aggressive"}
                      placement="top"
                      overlay={
                        <Tooltip id={`tooltip-aggressive`}>
                          Animal is aggressive
                        </Tooltip>
                      }
                    >
                      <FontAwesomeIcon icon={faClawMarks} size="sm" className="ms-1" />
                    </OverlayTrigger> :
                  animal.aggressive === 'no' ?
                    <OverlayTrigger
                      key={"not-aggressive"}
                      placement="top"
                      overlay={
                        <Tooltip id={`tooltip-aggressive`}>
                          Animal is not aggressive
                        </Tooltip>
                      }
                    >
                      <span className="fa-layers" style={{marginLeft:"2px"}}>
                        <FontAwesomeIcon icon={faClawMarks} size="sm" />
                        <FontAwesomeIcon icon={faBan} color="#ef5151" size="sm" transform={'shrink-2'} />
                      </span>
                    </OverlayTrigger> :
                  ""}
                  {animal.aco_required === 'yes' ?
                    <OverlayTrigger
                      key={"aco-required"}
                      placement="top"
                      overlay={
                        <Tooltip id={`tooltip-aco-required`}>
                          Special Handling Required
                        </Tooltip>
                      }
                    >
                      <FontAwesomeIcon icon={faHandsHolding} size="sm" className="ms-1" />
                    </OverlayTrigger>
                  : ""}
                  {animal.injured === 'yes' ?
                    <OverlayTrigger
                      key={"injured"}
                      placement="top"
                      overlay={
                        <Tooltip id={`tooltip-injured`}>
                          Animal is injured
                        </Tooltip>
                      }
                    >
                      <FontAwesomeIcon icon={faMedkit} size="sm" className="ms-1 fa-move-up" />
                    </OverlayTrigger> :
                  animal.injured === 'no' ?
                    <OverlayTrigger
                      key={"not-injured"}
                      placement="top"
                      overlay={
                        <Tooltip id={`tooltip-injured`}>
                          Animal is not injured
                        </Tooltip>
                      }
                    >
                      <span className="fa-layers" style={{marginLeft:"2px"}}>
                        <FontAwesomeIcon icon={faMedkit} size="sm" className="fa-move-up" />
                        <FontAwesomeIcon icon={faBan} color="#ef5151" size="sm" transform={'shrink-2'} />
                      </span>
                    </OverlayTrigger> :
                  ""}
                  {animal.fixed === 'yes' ?
                    <OverlayTrigger
                      key={"fixed"}
                      placement="top"
                      overlay={
                        <Tooltip id={`tooltip-fixed`}>
                          Animal is altered or neutered
                        </Tooltip>
                      }
                    >
                      <FontAwesomeIcon icon={faCut} size="sm" className="ms-1" />
                    </OverlayTrigger> :
                  animal.fixed === 'no' ?
                  <OverlayTrigger
                    key={"not-fixed"}
                    placement="top"
                    overlay={
                      <Tooltip id={`tooltip-fixed`}>
                        Animal is not altered or neutered
                      </Tooltip>
                    }
                  >
                    <span className="fa-layers" style={{marginLeft:"2px"}}>
                      <FontAwesomeIcon icon={faCut} size="sm" />
                      <FontAwesomeIcon icon={faBan} color="#ef5151" size="sm" transform={'shrink-2'} />
                    </span>
                  </OverlayTrigger> :
                  ""}
                  {animal.medical_record ?
                    <OverlayTrigger
                      key={"medical-record"}
                      placement="top"
                      overlay={
                        <Tooltip id={`tooltip-medical-record`}>
                          Animal has a medical record
                        </Tooltip>
                      }
                    >
                      <FontAwesomeIcon icon={faFolderMedical} className="ms-1" size="sm" inverse />
                    </OverlayTrigger> :
                  ""}
                  {animal.public === true ?
                    <OverlayTrigger
                      key={"public"}
                      placement="top"
                      overlay={
                        <Tooltip id={`tooltip-public`}>
                          Animal is public
                        </Tooltip>
                      }
                    >
                      <FontAwesomeIcon icon={faGlobe} size="sm" className="ms-1 fa-move-up" transform={'shrink-2'} />
                    </OverlayTrigger> : ""
                    }
                </Card.Title>
                <Scrollbar style={{height:"159px", marginBottom:"-16px"}} renderThumbHorizontal={props => <div {...props} style={{...props.style, display: 'none'}} />}>
                  <ListGroup>
                    <ListGroup.Item>{titleCase(animal.species_string_singular)}{animal.size ? <span>,&nbsp;{titleCase(animal.size)}</span> : ""}{animal.sex ? <span>,&nbsp;{titleCase(animal.sex)}</span> : ""}{animal.age ? <span>,&nbsp;{titleCase(animal.age)}</span> : ""}{animal.pcolor ? <span style={{textTransform:"capitalize"}}>,&nbsp;{animal.pcolor}{animal.scolor ? <span>/{animal.scolor}</span> : ""}</span> : ""}</ListGroup.Item>
                    <ListGroup.Item><b>Status: </b> {animal.status}</ListGroup.Item>
                    {options.vet_status.includes('Untriaged') || (animal.vet_status !== 'Untriaged') ? <ListGroup.Item><b>Vet Status: </b> {animal.vet_status}</ListGroup.Item> : ""}
                    {animal.microchip ? <ListGroup.Item><b>Microchip: </b> {animal.microchip || "N/A"}</ListGroup.Item> : ""}
                    {animal.external_id ? <ListGroup.Item><b>External ID: </b> {animal.external_id || "N/A"}</ListGroup.Item> : ""}
                    {animal.owners.map(owner => (
                      <ListGroup.Item key={owner.id}>
                        <b>Owner:</b> <Link href={"/" + organization + "/" + incident + "/people/owner/" + owner.id} className="text-link" style={{textDecoration:"none", color:"white"}}>{owner.first_name} {owner.last_name}</Link>
                        {owner.display_phone ?
                        <OverlayTrigger
                          key={"owner-phone"}
                          placement="top"
                          overlay={
                            <Tooltip id={`tooltip-owner-phone`}>
                              Phone: {owner.display_phone}
                            </Tooltip>
                          }
                        >
                          <FontAwesomeIcon icon={faPhoneRotary} className="ms-1" inverse />
                        </OverlayTrigger>
                        : ""}
                        {owner.email ?
                        <OverlayTrigger
                          key={"owner-email"}
                          placement="top"
                          overlay={
                            <Tooltip id={`tooltip-owner-email`}>
                              Email: {owner.email}
                            </Tooltip>
                          }
                        >
                          <FontAwesomeIcon icon={faEnvelope} className="ms-1" inverse />
                        </OverlayTrigger>
                        : ""}
                      </ListGroup.Item>
                    ))}
                    {animal.reporter ? <ListGroup.Item><b>Reporter: </b><Link href={"/" + organization + "/" + incident + "/people/reporter/" + animal.reporter} className="text-link" style={{textDecoration:"none", color:"white"}}>{animal.reporter_object.first_name} {animal.reporter_object.last_name}</Link></ListGroup.Item> : ""}
                  </ListGroup>
                </Scrollbar>
              </Card.Body>
            </Card>
            <Card style={{marginBottom:"6px"}}>
              <Card.Body>
              <Card.Title style={{marginTop:"-9px", marginBottom:"8px"}}>Location</Card.Title>
                <ListGroup>
                  <ListGroup.Item className='request'><b>Service Request: </b>{animal.request ? <Link href={"/" + organization + "/" + incident + "/hotline/servicerequest/" + animal.request_id_for_incident} className="text-link" style={{textDecoration:"none", color:"white"}}>{animal.request_address}</Link> : "None"}</ListGroup.Item>
                  <ListGroup.Item><b>Shelter: </b>{animal.shelter ? <Link href={"/" + organization + "/" + incident + "/shelter/" + animal.shelter} className="text-link" style={{textDecoration:"none", color:"white"}}>{animal.shelter_object.name}</Link> : "None"}
                    {/* {animal.shelter ?
                    <span>
                      <OverlayTrigger
                        key={"animal-intake-date"}
                        placement="top"
                        overlay={
                          <Tooltip id={`tooltip-animal-intake-date`}>
                            Intake Date: <Moment format="MMMM Do YYYY HH:mm">{animal.intake_date}</Moment>
                          </Tooltip>
                        }
                      >
                        <FontAwesomeIcon icon={faCalendarDay} className="ms-1" inverse />
                      </OverlayTrigger>
                      {animal.shelter_object.phone ?
                      <OverlayTrigger
                        key={"animal-shelter-phone"}
                        placement="top"
                        overlay={
                          <Tooltip id={`tooltip-animal-shelter-phone`}>
                            Phone: {animal.shelter_object.display_phone}
                          </Tooltip>
                        }
                      >
                        <FontAwesomeIcon icon={faPhoneRotary} className="ms-1" inverse />
                      </OverlayTrigger>
                      : ""}
                      <OverlayTrigger
                        key={"animal-shelter-address"}
                        placement="top"
                        overlay={
                          <Tooltip id={`tooltip-animal-shelter-address`}>
                            Address: {animal.shelter_object.full_address}
                          </Tooltip>
                        }
                      >
                        <FontAwesomeIcon icon={faClipboardList} className="ms-1" inverse />
                      </OverlayTrigger>
                    </span>
                    : ""} */}
                  </ListGroup.Item>
                  {animal.room ? <ListGroup.Item>
                    <b>Building - Room: </b>
                    <Link href={"/" + organization + "/" + incident + "/shelter/building/" + animal.building_id} className="text-link" style={{textDecoration:"none", color:"white"}}>{animal.building_name}</Link>
                    - 
                    <Link href={"/" + organization + "/" + incident + "/shelter/room/" + animal.room} className="text-link" style={{textDecoration:"none", color:"white"}}>{animal.room_name}</Link></ListGroup.Item> : ""}
                </ListGroup>
              </Card.Body>
            </Card>
          </CardGroup>
        </div>
      ))}
      <p style={{marginTop:"15px"}}>{data.isFetching ? 'Fetching Animals...' : <span>{data.animals.length === 0 ? 'No animals found.' : ''}</span>}</p>
      <Row className="mb-3">
        <Col xs={12} className="ps-0">
          <Paginator page={page} numPages={numPages} setPage={setPage} />
        </Col>
      </Row>
      <Modal show={showNotFound} onHide={() => {setShowNotFound(false)}}>
        <Modal.Header closeButton>
          <Modal.Title>Animal Not Found</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            A#{goToID} does not exist for this incident.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => {setShowNotFound(false)}}>Ok</Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

export default AnimalSearch;
